'use client';

import type { DetailHomeGiftProductResponse } from '__generated__/api/unauth';
import { Divider, Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import { cn } from '@core/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { UnauthQueryOptions } from '@/api';
import { useIsDonationHistory } from '@/app/donation/_components/DonationButton/_hooks/useIsDonationHistory';
import { AgreementSection } from '@/app/donation/_components/DonationButton/AgreementSection';
import { PageNavigation } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { DONATION_ROUTES, STORAGE_KEY } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useScrollTop } from '@/hooks/useScrollTop';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { useGiftCart } from '../../_hooks/useGiftCart';
import type { OptionSetWithQuantity } from './_components';
import { CartBottomCTA, CartEmptyState, CartGroup, DeleteConfirmModal, DonationComparisonCard, OptionSelectBottomSheet, RecommendedGiftsContainer, RecommendedGiftsSkeleton } from './_components';
import { DONATION_COMPARISON_TEXTS } from './_constants';
import { useCartSelection } from './_hooks/useCartSelection';
import type { CartItemData, CartOrderStorage, DeleteModalState, OptionChangeParams } from './_types';
import { CART_ORDER_STORAGE_KEY } from './order/_constants';
const CartPage = () => {
  useScrollTop();
  const router = useCustomRouter();
  const queryClient = useQueryClient();
  const {
    toast
  } = useToast();
  const {
    institution,
    items,
    productGroups,
    totalPrice,
    isLoading,
    increaseQuantity,
    decreaseQuantity,
    updateOptions,
    addItem,
    deleteItems
  } = useGiftCart({
    staleTime: 0,
    gcTime: 1000 * 60 * 5 // 5분
  });
  const hasTrackedViewCart = useRef(false);
  useEffect(() => {
    if (hasTrackedViewCart.current || isLoading) return;
    hasTrackedViewCart.current = true;
    trackGtmEvent(GTM_EVENT_TYPE.VIEW_CART, {
      currency: 'KRW',
      value: totalPrice,
      items: items.map(item => ({
        item_type: '답례품' as const,
        price: item.requiredDonationAmount + item.extraPrice,
        item_name: item.productName,
        quantity: item.quantity,
        item_id: item.productNumber
      }))
    });
  }, [isLoading, items, totalPrice]);

  // 선택 상태 관리
  const {
    selectedIds,
    selectedCount,
    selectedTotalAmount,
    select: handleSelect,
    selectAll: handleSelectAll
  } = useCartSelection({
    items,
    totalPrice
  });
  const selectedGiftBenefitAmount = useMemo(() => items.filter(item_0 => selectedIds.has(item_0.giftCartItemOptionId)).reduce((sum, item_1) => sum + item_1.originItemTotalPrice, 0), [items, selectedIds]);
  const {
    isDonationHistory
  } = useIsDonationHistory();
  const [showAgreement, setShowAgreement] = useState(false);

  // 옵션 시트 상태
  const [optionSheet, setOptionSheet] = useState<{
    product: DetailHomeGiftProductResponse | null;
    isEditMode: boolean;
    editingItem: CartItemData | null;
  }>({
    product: null,
    isEditMode: false,
    editingItem: null
  });

  // 삭제 모달 상태
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    opened: false,
    giftCartItemIds: [],
    count: 1
  });

  // 옵션 변경 (수량 + 옵션 내용)
  const handleChangeOption = useCallback(async (giftCartItemOptionId: number, changes: OptionChangeParams) => {
    const item_2 = items.find(cartItem => cartItem.giftCartItemOptionId === giftCartItemOptionId);
    if (!item_2) return;

    // 옵션 내용 변경 (바텀시트 열기)
    if (changes.optionItemIds !== undefined) {
      // 상품 상세 정보 가져오기
      try {
        const productDetail = await queryClient.fetchQuery({
          ...UnauthQueryOptions['get:/wello/v2/allows/home/gift-product/{productNumber}']({
            path: {
              productNumber: item_2.productNumber
            }
          }),
          staleTime: 1000 * 60 * 5
        });
        if (productDetail?.context) {
          setOptionSheet({
            product: productDetail.context,
            isEditMode: true,
            editingItem: item_2
          });
        }
      } catch {
        toast({
          message: '상품 정보를 불러오는데 실패했어요',
          type: 'fail'
        });
      }
      return;
    }

    // 수량 변경
    if (changes.quantity !== undefined) {
      const currentQuantity = item_2.quantity;
      const newQuantity = changes.quantity;
      if (newQuantity > currentQuantity) {
        await increaseQuantity(giftCartItemOptionId);
      } else if (newQuantity < currentQuantity && newQuantity >= 1) {
        await decreaseQuantity(giftCartItemOptionId);
      }
    }
  }, [items, queryClient, toast, increaseQuantity, decreaseQuantity]);

  // 삭제 모달 열기 (giftCartItemIds를 받아서 모달만 열어줌)
  // 삭제 모달 열기 (delete API)
  const requestDelete = useCallback((giftCartItemIds: number[]) => {
    if (giftCartItemIds.length === 0) return;
    const giftCartItemIdSet = new Set(giftCartItemIds);
    const totalQuantity = items.filter(item_3 => giftCartItemIdSet.has(item_3.giftCartItemId)).reduce((sum_0, item_4) => sum_0 + item_4.quantity, 0);
    setDeleteModal({
      opened: true,
      giftCartItemIds,
      count: totalQuantity
    });
  }, [items]);

  // 단일 옵션 삭제 모달 열기 (decrease API)
  const requestDecreaseDelete = useCallback((giftCartItemOptionId_0: number) => {
    const item_5 = items.find(cartItem_0 => cartItem_0.giftCartItemOptionId === giftCartItemOptionId_0);
    if (!item_5) return;
    setDeleteModal({
      opened: true,
      giftCartItemIds: [item_5.giftCartItemId],
      count: 1,
      decreaseOptionId: giftCartItemOptionId_0
    });
  }, [items]);

  // 선택된 아이템 삭제
  const requestSelectedDelete = useCallback(() => {
    const selectedItems = items.filter(item_6 => selectedIds.has(item_6.giftCartItemOptionId));
    requestDelete([...new Set(selectedItems.map(item_7 => item_7.giftCartItemId))]);
  }, [items, selectedIds, requestDelete]);

  // 삭제 확인
  const handleDeleteConfirm = useCallback(async () => {
    if (deleteModal.giftCartItemIds.length === 0) return;
    const deletedItemIdSet = new Set(deleteModal.giftCartItemIds);
    const removedItems = deleteModal.decreaseOptionId ? items.filter(item_8 => item_8.giftCartItemOptionId === deleteModal.decreaseOptionId).map(item_9 => ({
      ...item_9,
      quantity: 1
    })) : items.filter(item_10 => deletedItemIdSet.has(item_10.giftCartItemId));
    try {
      if (deleteModal.decreaseOptionId) {
        await decreaseQuantity(deleteModal.decreaseOptionId);
      } else {
        await deleteItems(deleteModal.giftCartItemIds);
      }
      trackGtmEvent(GTM_EVENT_TYPE.REMOVE_FROM_CART, {
        currency: 'KRW',
        value: removedItems.reduce((sum_1, item_11) => sum_1 + (item_11.requiredDonationAmount + item_11.extraPrice) * item_11.quantity, 0),
        items: removedItems.map(item_12 => ({
          item_type: '답례품' as const,
          price: item_12.requiredDonationAmount + item_12.extraPrice,
          item_name: item_12.productName,
          quantity: item_12.quantity,
          item_id: item_12.productNumber
        }))
      });
      toast({
        message: `${deleteModal.count}개의 답례품을 삭제했습니다`
      });
    } catch {
      toast({
        message: '답례품 삭제에 실패했어요',
        type: 'fail'
      });
      return;
    }
    setDeleteModal({
      opened: false,
      giftCartItemIds: [],
      count: 1
    });

    // 옵션 시트가 열려있으면 함께 닫기
    if (optionSheet.product) {
      setOptionSheet({
        product: null,
        isEditMode: false,
        editingItem: null
      });
    }
  }, [deleteModal.giftCartItemIds, deleteModal.count, deleteModal.decreaseOptionId, items, deleteItems, decreaseQuantity, toast, optionSheet.product]);

  // 옵션 시트 닫기
  const closeOptionSheet = useCallback(() => {
    setOptionSheet({
      product: null,
      isEditMode: false,
      editingItem: null
    });
  }, []);

  // 옵션 시트에서 추천 상품 열기
  const openOptionSheet = useCallback((product: DetailHomeGiftProductResponse) => {
    setOptionSheet({
      product,
      isEditMode: false,
      editingItem: null
    });
  }, []);

  // 옵션 확인 (추가 또는 수정)
  const handleOptionConfirm = useCallback(async (optionSets: OptionSetWithQuantity[]) => {
    if (!optionSheet.product) return;
    if (optionSheet.isEditMode && optionSheet.editingItem) {
      // 수정 모드: 옵션 업데이트
      const optionSet = optionSets[0];
      if (optionSet) {
        const optionItemIds = optionSet.options.map(o => o.optionItemId);
        await updateOptions(optionSheet.editingItem.giftCartItemOptionId, optionItemIds, optionSet.quantity);
        await queryClient.invalidateQueries();
      }
    } else {
      // 추가 모드: 새 아이템 추가
      const optionSet_0 = optionSets[0];
      if (optionSet_0 && optionSheet.product?.productNumber) {
        const optionItemIds_0 = optionSet_0.options.map(o_0 => o_0.optionItemId);
        try {
          await addItem({
            giftProductNumber: optionSheet.product.productNumber,
            optionItemIds: optionItemIds_0.length > 0 ? optionItemIds_0 : undefined,
            quantity: optionSet_0.quantity
          });
          toast({
            message: '장바구니에 담았어요'
          });
        } catch {
          toast({
            message: '장바구니 담기에 실패했어요',
            type: 'fail'
          });
        }
      }
    }
    closeOptionSheet();
  }, [optionSheet, updateOptions, addItem, toast, closeOptionSheet, queryClient]);

  // 주문 페이지로 이동
  const navigateToOrder = useCallback(() => {
    if (!institution) return;
    const cartOrderData: CartOrderStorage = {
      selectedOptionIds: Array.from(selectedIds),
      institutionId: institution.id,
      institutionName: institution.name,
      institutionLogoUrl: institution.logoUrl
    };
    trackGtmEvent(GTM_EVENT_TYPE.START_GIFT, {
      local_name: institution.name
    });
    localStorage.setItem(CART_ORDER_STORAGE_KEY, JSON.stringify(cartOrderData));
    router.push(DONATION_ROUTES.CART_ORDER.pathname);
  }, [selectedIds, institution, router]);

  // 기부하기 클릭
  const handleDonateClick = useCallback(() => {
    if (selectedIds.size === 0) {
      toast({
        message: '답례품을 선택해주세요',
        type: 'fail'
      });
      return;
    }
    if (!institution) return;

    // 동의 체크
    const isAgreement = sessionStorage.getItem(STORAGE_KEY.DONATION_AGREEMENT);
    if (!isDonationHistory && !isAgreement) {
      setShowAgreement(true);
      return;
    }
    navigateToOrder();
  }, [selectedIds, institution, toast, isDonationHistory, navigateToOrder]);
  const initialOptionSets = useMemo(() => {
    if (!optionSheet.isEditMode || !optionSheet.editingItem) return [];
    const item_13 = optionSheet.editingItem;
    const optionGroups = optionSheet.product?.optionGroupInfoList ?? [];
    const options = item_13.optionItemIds.map(optionItemId => {
      const group = optionGroups.find(g => g.optionItemInfoList?.some(o_1 => o_1.optionItemId === optionItemId));
      const optionItem = group?.optionItemInfoList?.find(o_2 => o_2.optionItemId === optionItemId);
      return {
        optionGroupId: group?.optionGroupId ?? 0,
        optionItemId,
        optionItemName: optionItem?.itemName ?? '',
        optionGroupName: group?.optionGroupName ?? '',
        extraPrice: optionItem?.extraPrice ?? 0
      };
    });
    return [{
      options,
      quantity: item_13.quantity
    }];
  }, [optionSheet]);
  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-Gray-white">
        <Spinner size={24} />
      </div>;
  }
  if (!institution || items.length === 0) {
    return <div className="bg-Gray-white">
        <PageNavigation title="답례품 장바구니" hideHomeButton />
        <CartEmptyState />
      </div>;
  }
  return <div className={cn('w-full bg-Gray-25 pt-60', 'mobile-tablet:bg-Gray-white mobile-tablet:pt-0')}>
      <div className={cn('flex flex-col gap-x-32 gap-y-40 px-40 pb-210 content-container', 'mobile-tablet:gap-y-0 mobile-tablet:px-0')}>
        <div className="flex flex-1 flex-col gap-40 mobile-tablet:gap-28">
          <PageNavigation title="답례품 장바구니" hideHomeButton className="z-20 mobile-tablet:px-0" />

          <CartGroup institution={{
          id: institution.id,
          name: institution.name,
          logoUrl: institution.logoUrl
        }} productGroups={productGroups} selectedIds={selectedIds} onSelect={handleSelect} onSelectAll={handleSelectAll} onChangeOption={handleChangeOption} onDelete={requestDecreaseDelete} onDeleteProduct={giftCartItemId => requestDelete([giftCartItemId])} onDeleteSelected={requestSelectedDelete} />

          <DonationComparisonCard minBenefit={DONATION_COMPARISON_TEXTS.minBenefit} maxBenefit={DONATION_COMPARISON_TEXTS.maxBenefit} className="mobile-tablet:px-20" />

          <Divider className="hidden h-10 bg-Gray-100 mobile-tablet:block" />

          <ErrorBoundary fallback={null}>
            <Suspense fallback={<RecommendedGiftsSkeleton />}>
              <RecommendedGiftsContainer institution={institution} onOpenOptionSheet={openOptionSheet} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>

      <CartBottomCTA donationAmount={selectedTotalAmount} selectedCount={selectedCount} giftBenefitAmount={selectedGiftBenefitAmount} onDonateClick={handleDonateClick} isHidden={!!optionSheet.product || showAgreement} />

      {optionSheet.product && <OptionSelectBottomSheet opened={!!optionSheet.product} onClose={closeOptionSheet} productData={optionSheet.product} onConfirm={handleOptionConfirm} isEditMode={optionSheet.isEditMode} initialOptionSets={initialOptionSets} onDelete={() => {
      if (optionSheet.editingItem) {
        requestDelete([optionSheet.editingItem.giftCartItemId]);
      }
    }} />}

      <DeleteConfirmModal opened={deleteModal.opened} onClose={() => setDeleteModal({
      opened: false,
      giftCartItemIds: [],
      count: 1
    })} onConfirm={handleDeleteConfirm} count={deleteModal.count} />

      <ResponsiveDialog opened={showAgreement} onClose={() => setShowAgreement(false)} drawerType="bottom" modalHeader="고향사랑기부 서비스를 이용하기 위해 꼭 필요한 동의만 추렸어요" title="고향사랑기부 서비스를 이용하기 위해 꼭 필요한 동의만 추렸어요">
        <AgreementSection onNext={() => {
        sessionStorage.setItem(STORAGE_KEY.DONATION_AGREEMENT, 'true');
        setShowAgreement(false);
        navigateToOrder();
      }} />
      </ResponsiveDialog>
    </div>;
};
export default CartPage;